import ActivityReportTableDataLoader from 'containers/activity-reports/types/ActivityReportTableDataLoader';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { LaborMetricsInterval } from '__generated__/graphql';
import { ActivityReportType } from '../../ActivityReportFilter/ActivityReportFilter';
import { IMemberActivityTableRowInfo } from './useMemberActivity';
import useMemberActivityBasicDetails from './useMemberActivityBasicDetails';
import useMemberActivityCostCodeDetails from './useMemberActivityCostCodeDetails';
import useMemberActivityDateRangeDetails from './useMemberActivityDateRangeDetails';
import useMemberActivityEquipmentDetails from './useMemberActivityEquipmentDetails';
import useMemberActivityProjectDetails from './useMemberActivityProjectDetails';

export default function useMemberActivityDetails(
  filterId: string,
  filterType: ActivityReportType,
  secondaryFilterType: ActivityReportType,
  timeRange: ITimeRange<DateTime>,
  filterIdWithDescendants?: boolean,
  dateRangeReportType?: TimeRangeType
): ActivityReportTableDataLoader<IMemberActivityTableRowInfo> {
  const [isLoading, setIsLoading] = useState(false);

  const dateRangeInterval = useMemo(() => {
    if (secondaryFilterType === ActivityReportType.BY_DAY) {
      return LaborMetricsInterval.Day;
    }

    if (dateRangeReportType === TimeRangeType.PAY_PERIOD) {
      return LaborMetricsInterval.PayPeriod;
    }

    if (dateRangeReportType === TimeRangeType.MONTHLY) {
      return LaborMetricsInterval.Month;
    }

    return LaborMetricsInterval.OvertimePeriod;
  }, [secondaryFilterType, dateRangeReportType]);

  const basicDetails = useMemberActivityBasicDetails(filterId, filterType, timeRange, filterIdWithDescendants);
  const costCodeDetails = useMemberActivityCostCodeDetails(filterId, filterType, timeRange, filterIdWithDescendants);
  const equipmentDetails = useMemberActivityEquipmentDetails(filterId, filterType, timeRange, filterIdWithDescendants);
  const projectDetails = useMemberActivityProjectDetails(filterId, filterType, timeRange);
  const dateRangeDetails = useMemberActivityDateRangeDetails(
    filterId,
    filterType,
    timeRange,
    dateRangeInterval,
    filterIdWithDescendants
  );

  const dataLoader = useMemo(() => {
    if (secondaryFilterType === ActivityReportType.BASIC) {
      return basicDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_COST_CODE) {
      return costCodeDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_DATE_RANGE) {
      return dateRangeDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_DAY) {
      return dateRangeDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_EQUIPMENT) {
      return equipmentDetails;
    } else if (secondaryFilterType === ActivityReportType.BY_PROJECT) {
      return projectDetails;
    } else {
      throw Error('Not supported secondary report type when loading members: ' + secondaryFilterType);
    }
  }, [basicDetails, costCodeDetails, equipmentDetails, projectDetails, dateRangeDetails]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, filterType, filterId, secondaryFilterType, dateRangeInterval]);

  async function loadData() {
    setIsLoading(true);
    await dataLoader.loadData();
    setIsLoading(false);
  }

  return {
    isLoading: isLoading,
    sortedData: dataLoader.sortedData,
    onSort: dataLoader.onSort,
    sortedBy: dataLoader.sortedBy as keyof IMemberActivityTableRowInfo,
    sortDirection: dataLoader.sortDirection,
    sortIsDirty: dataLoader.sortIsDirty,
    remainingData: dataLoader.remainingData,
  };
}
